import type { TemplateFunction, TemplateResult } from 'Components/types';
import { logTemplateError } from 'Diagnostics/diagnostics';

/**
 * Wraps template.call() in a try/catch and sends a structured log entry to
 * Loki via the diagnostics module if the template throws.
 *
 * On error, returns null so the component gracefully disappears — the same
 * behaviour as an ErrorBoundary — while still producing precise per-component
 * diagnostics instead of a generic component-stack trace.
 *
 * Deduplication is handled inside logTemplateError (by componentName + message)
 * so repeated renders of a broken template produce only one Loki entry.
 */
export default function safeTemplateCall<Params, RootProps extends object, RootElement extends HTMLElement>(
  template: TemplateFunction<Params, RootProps, RootElement>,
  props: Params,
  componentName: string,
  itemId?: string | number,
): TemplateResult<RootProps, RootElement> {
  try {
    return template.call(props as Parameters<typeof template.call>[0]);
  } catch (err) {
    console.error(`[${componentName}] template.call() failed`, err);
    try {
      logTemplateError({ componentName, itemId: itemId ?? '', error: err });
    } catch {
      // diagnostics must never break the app
    }
    return null;
  }
}
